import { CheckOutlined, ExclamationCircleTwoTone } from '@ant-design/icons';
import { Button, Empty, Spin } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { OpenClassBookingOrderSummary } from '../../openClasses/types/OpenClass';
import { getOpenClassBookingOrderPath } from '../utils/openClassBookingOrder';

type OpenClassBookingOrderResultsProps = {
  loading: boolean;
  openClassBookingOrders: OpenClassBookingOrderSummary[];
};

const OpenClassBookingOrderResults: React.FC<
  OpenClassBookingOrderResultsProps
> = ({ loading, openClassBookingOrders }) => {
  const { t } = useTranslation();
  const location = useLocation();

  if (loading && openClassBookingOrders.length === 0) {
    return (
      <div className="flex justify-center pt-10">
        <Spin size="large" />
      </div>
    );
  }

  if (openClassBookingOrders.length === 0) {
    return (
      <Empty
        className="pt-3"
        description={t('views.BookingOrders.noOpenClassOrders')}
        image={Empty.PRESENTED_IMAGE_DEFAULT}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {openClassBookingOrders.map((summary) => {
        const { order } = summary;
        const incomplete = summary.unassignedParticipantCount > 0;

        return (
          <div
            className="rounded-lg bg-white p-4 text-black shadow-md"
            key={order.id}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 grow">
                <div className="flex items-center gap-2 truncate text-base font-medium">
                  {incomplete ? (
                    <ExclamationCircleTwoTone twoToneColor="#faad14" />
                  ) : (
                    <CheckOutlined className="text-safeLifeMedium" />
                  )}
                  <span>
                    {order.clientCompany?.name ??
                      order.contact?.name ??
                      t('views.OpenClassBookingOrderDetails.standaloneOrder')}
                  </span>
                </div>
                <div className="mt-1 text-sm text-gray-500">
                  {t('views.BookingOrders.booked', {
                    date: dayjs(order.orderDate).format('YYYY-MM-DD'),
                  })}
                  {' · '}
                  {t('views.BookingOrders.attendees', {
                    count: summary.participantCount,
                  })}
                  {order.comment && ` · ${order.comment}`}
                </div>
              </div>
              <Link
                state={{ prevPage: location.pathname }}
                to={getOpenClassBookingOrderPath(order.id)}>
                <Button>{t('common.open')}</Button>
              </Link>
            </div>
            <div className="my-3 h-px bg-gray-400" />
            <div className="flex flex-wrap justify-between gap-x-8 gap-y-3 text-sm">
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                {summary.classes.map((openClass) => (
                  <div
                    className="flex items-center gap-2"
                    key={openClass.openClassId}>
                    <span className="h-2 w-2 shrink-0 rounded-full bg-safeLifeMedium" />
                    <span className="truncate font-medium">
                      {openClass.label}
                    </span>
                  </div>
                ))}
              </div>
              <div
                className={
                  incomplete
                    ? 'flex items-center gap-1 text-black'
                    : 'flex items-center gap-1 text-gray-500'
                }>
                {incomplete ? (
                  <ExclamationCircleTwoTone twoToneColor="#faad14" />
                ) : (
                  <CheckOutlined className="text-safeLifeMedium" />
                )}
                <span>
                  {incomplete
                    ? t('views.BookingOrders.attendeesWithoutClass', {
                        count: summary.unassignedParticipantCount,
                      })
                    : t('views.BookingOrders.noAttendeesWithoutClass')}
                </span>
              </div>
            </div>
          </div>
        );
      })}
      {loading && (
        <div className="flex justify-center py-2">
          <Spin />
        </div>
      )}
    </div>
  );
};

export default OpenClassBookingOrderResults;
